import ContentWrapper from "@components/ContentWrapper/ContentWrapper";
import { Button, FCX, getRem } from "@datassential/platform-ui-lib";
import { useCommonDataContext, useKeyword, useMetrics } from "@core/CommonDataContext";
import Loader from "@components/Loader";
import { useEffect, useMemo, useState } from "react";
import { cleanUpMetricName, CORRELATION_METRICS_ORDER, Metric } from "@models/Metric";
import SelectFiltersProvider from "@vms/SelectFilters/SelectFiltersContext";
import ContentWidget from "@components/ContentWidget/ContentWidget";
import CorrelationHeader from "@pages/Correlation/CorrelationHeader";
import CorrelationMainFilters from "@pages/Correlation/CorrelationMainFilters";
import CorrelationSubFilters from "@pages/Correlation/CorrelationSubFilters";
import CorrelationTable from "@pages/Correlation/CorrelationTable";
import {
    CorrelationExportRequest,
    CorrelationRequest,
    useCorrelation,
    useCorrelationExport
} from "@api/useCorrelation";
import useQueryState from "@hooks/useQueryState";
import { toast } from "react-toastify";
import SelectedKeywords from "@vms/SelectedKeywords";
import ActionsBar from "@vms/ActionsBar";
import {
    SelectedKeywordsProvider,
    useSelectedKeywordsContext
} from "@vms/SelectedKeywords/SelectedKeywordsContext";
import SelectedKeywordsViewsTabs from "@vms/SelectedKeywords/SelectedKeywordsViewsTabs";

const CorrelationWithContext = () => {
    const [paramKeywordId] = useQueryState('keywordId');
    const {
        selectedKeywordsIds,
        setSelectedKeywordsIds,
    } = useSelectedKeywordsContext();
    const [selectedMetric, setSelectedMetric] = useState<Metric>(Metric.LoveLikeIt);
    const [mainKeywordId, setMainKeywordId] = useState<number | null>(+paramKeywordId || null);
    const [selectedCorrelatingMetric, setSelectedCorrelatingMetric] = useState<Metric | null>(null);
    const [search, setSearch] = useState<string>("");

    const metrics = useMetrics();

    const metricOptions = useMemo(
        () => {
            return CORRELATION_METRICS_ORDER.map((metricId) => ({
                id: metricId,
                title: cleanUpMetricName(metrics.find((m) => m.id === metricId)?.name ?? ''),
            }));
        },
        [metrics]
    );

    const selectedMetricName = useMemo(
        () => {
            return metricOptions.find(option => option.id === selectedMetric)?.title?.toUpperCase() ?? ''
        },
        [metricOptions, selectedMetric]
    );

    const relatingMetricName = useMemo(
        () => metricOptions.find(
            option => option.id === selectedCorrelatingMetric
        )?.title?.toUpperCase() ?? ''
        ,
        [metricOptions, selectedCorrelatingMetric]
    );

    const keyword = useKeyword(mainKeywordId);

    const keywordName = useMemo(
        () => keyword?.name?.toUpperCase() ?? "",
        [keyword]
    );

    const payload: CorrelationRequest = useMemo(
        () => ({
            metric: selectedMetric as CorrelationRequest['metric'],
            keywordId: mainKeywordId ?? undefined,
        }),
        [selectedMetric, mainKeywordId]
    );

    const {
        isLoading,
        data: rawApiData,
    } = useCorrelation(payload)

    useEffect(() => {
        if (paramKeywordId && !isLoading && !rawApiData.tableData.length) {
            setMainKeywordId((prev) => {
                if (prev) {
                    toast.error("Please select valid keyword")
                    return null;
                }

                return prev;
            })
        }
    }, [paramKeywordId, isLoading, rawApiData]);

    useEffect(() => {
        setSelectedKeywordsIds([]);
    }, [mainKeywordId, selectedMetric, setSelectedKeywordsIds]);

    const selectedHeadline = useMemo(
        () => selectedMetricName && keywordName
            ? (<>
                {selectedMetricName} <strong className="Correlation__bold-text">{keywordName}</strong>
            </>)
            : null,
        [selectedMetricName, keywordName])
    const relatingHeadline = useMemo(() => relatingMetricName ?? "", [relatingMetricName])

    const exportPayload: CorrelationExportRequest = useMemo(
        () => ({
            metric: selectedMetric as CorrelationExportRequest["metric"],
            metricToDisplay: selectedCorrelatingMetric as CorrelationExportRequest["metricToDisplay"],
            keywordId: mainKeywordId ?? undefined,
            keywordsIdsToDisplay: selectedKeywordsIds,
        }),
        [selectedMetric, selectedCorrelatingMetric, mainKeywordId, selectedKeywordsIds]
    );

    const {
        isLoading: isReportLoading,
        get: getReport,
    } = useCorrelationExport(exportPayload);

    const canExport = useMemo(
        (): boolean => {
            return !!exportPayload.metric
                && !!exportPayload.metricToDisplay
                && !!exportPayload.keywordId
                && !!exportPayload.keywordsIdsToDisplay?.length
        },
        [exportPayload]
    );

    return (
        <ContentWrapper className="Correlation">
            <CorrelationHeader/>

            <ContentWidget
                className="Correlation__content appears-top del-1"
                testId="blockCorrelation"
            >
                <CorrelationMainFilters
                    selectedMetric={selectedMetric}
                    setSelectedMetric={setSelectedMetric}
                    mainKeywordId={mainKeywordId}
                    setMainKeywordId={setMainKeywordId}
                    metricOptions={metricOptions}
                    selectedMetricName={selectedMetricName}
                    keywordName={keywordName}
                    isLoading={isLoading}
                    ofTotalUs={rawApiData.raw ?? null}
                />
                {
                    mainKeywordId ? (
                        <>
                            <CorrelationSubFilters
                                selectedCorrelatingMetric={selectedCorrelatingMetric}
                                setSelectedCorrelatingMetric={setSelectedCorrelatingMetric}
                                metricOptions={metricOptions}
                                selectedHeadline={selectedHeadline}
                                search={search}
                                setSearch={setSearch}
                            />
                            <CorrelationTable
                                mainKeywordId={mainKeywordId}
                                selectedCorrelatingMetric={selectedCorrelatingMetric}
                                search={search}
                                isLoading={isLoading}
                                rawApiData={rawApiData}
                                selectedHeadline={selectedHeadline}
                                relatingHeadline={relatingHeadline}
                            />
                        </>
                    ) : null
                }
            </ContentWidget>
            {(selectedKeywordsIds.length > 0) && (
                <ActionsBar>
                    <div style={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'center',
                        justifyContent: "center",
                        gap: getRem(32),
                    }}>
                        <SelectedKeywordsViewsTabs className="appears-top del-1"/>
                        <SelectedKeywords
                            className="appears-top del-2"
                            style={{
                                width: getRem(190),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: "center",
                            }}
                        />
                        <Button
                            className="appears-top del-3"
                            isDisabled={!canExport || isReportLoading}
                            onClick={getReport}
                            style={{ width: getRem(150) }}
                        >
                            {isReportLoading && <div className="load-ellipsis">loading</div>}
                            {!isReportLoading && 'Download'}
                        </Button>
                    </div>
                </ActionsBar>
            )}
        </ContentWrapper>
    );
};

const Correlation: FCX = () => {
    const { isLoading } = useCommonDataContext();

    if (isLoading) {
        return <Loader/>;
    }

    return (
        <SelectedKeywordsProvider>
            <SelectFiltersProvider>
                <CorrelationWithContext/>
            </SelectFiltersProvider>
        </SelectedKeywordsProvider>
    )
}

export default Correlation;